import React, { useState, useEffect, useCallback, useRef } from "react";
import { addAdmitCard, deleteAdmitCard } from "../../api/api";
import { useGlobalData } from "../../context/GlobalDataContext";
import "../superadmin/AdminPanel.css";

function AdministrativeAdmitCardAdminPanel({ darkMode }) {
  const { threeTable, fetchThreeTable, fetchThreeTableSection, threeTableHasMore } = useGlobalData();
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const listRef = useRef(null);
  const PAGE_LIMIT = 29;
  const [jobs, setJobs] = useState([]);
  const [formData, setFormData] = useState({
    jobSlug: ""
  });
  const role = localStorage.getItem("role");

  // Initial load (global state)
  useEffect(() => {
    let ignore = false;
    async function fetchInitial() {
      setIsLoading(true);
      try {
        await fetchThreeTableSection("admitCards", false, { limit: PAGE_LIMIT, offset: 0 });
      } finally {
        if (!ignore) setIsLoading(false);
      }
    }
    fetchInitial();
    return () => { ignore = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Infinite scroll fetch
  const fetchMoreAdmitCards = useCallback(async () => {
    setIsFetchingMore(true);
    try {
      await fetchThreeTableSection("admitCards", false, { limit: PAGE_LIMIT, offset: threeTable.admitCards.length });
    } finally {
      setIsFetchingMore(false);
    }
  }, [fetchThreeTableSection, threeTable.admitCards.length]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!threeTableHasMore.admitCards || isFetchingMore) return;
    const el = listRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    if (rect.bottom <= window.innerHeight + 200) {
      fetchMoreAdmitCards();
    }
  }, [threeTableHasMore.admitCards, isFetchingMore, fetchMoreAdmitCards]);

  // Attach scroll event
  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // preload off hone ke baad jobs ensure karne ke liye
  useEffect(() => {
    if (!threeTable?.jobs?.length) {
      fetchThreeTable();
    }
  }, [threeTable?.jobs?.length, fetchThreeTable]);

  useEffect(() => {
    setJobs(threeTable.jobs || []);
  }, [threeTable.jobs]);


  const handleSubmit = async (e) => {
    e.preventDefault();
    await addAdmitCard({ ...formData, status: "pending" });
    alert("Admit Card added!");
    setFormData({ jobSlug: "" });
    // Refresh global state (replace with first page)
    await fetchThreeTableSection("admitCards", true, { limit: PAGE_LIMIT, offset: 0 });
  };

  const handleDelete = async (id) => {
    if (window.confirm("Delete this admit card?")) {
      await deleteAdmitCard(id);
      alert("Admit Card deleted!");
      // Refresh global state (replace with first page)
      await fetchThreeTableSection("admitCards", true, { limit: PAGE_LIMIT, offset: 0 });
    }
  };

  const getJobTitle = (jobSlug) =>
    jobs.find((job) => job.slug === jobSlug)?.title || jobSlug;

  const filteredAdmitCards = threeTable.admitCards.filter(
    (admitCard) => !formData.jobSlug || admitCard.jobSlug === formData.jobSlug
  );

  if (role !== "administrative") {
    return (
      <div className="admin-panel-error">
        ❌ Access denied! Only Administrative users can access this panel.
      </div>
    );
  }

  return (
    <div className="resource-admin-panel resource-admin-panel--orange">
      <div className="resource-admin-panel__header">
        <div>
          <h2 className="resource-admin-panel__heading">
            Administrative Admit Card Admin Panel
          </h2>
          <p className="resource-admin-panel__subtext">
            Job select karke admit card entry add karo, recent entries review karo aur live page ko direct open ya delete kar sakte ho.
          </p>
        </div>
        <div className="resource-admin-panel__pill">Admit Card</div>
      </div>

      <form className="resource-admin-panel__form" onSubmit={handleSubmit}>
        <div className="resource-admin-panel__field">
          <label>Job:</label>
          <select
            value={formData.jobSlug}
            onChange={(e) => setFormData({ ...formData, jobSlug: e.target.value })}
            required
          >
            <option value="">Select Job</option>
            {jobs.map((job) => (
              <option key={job.slug} value={job.slug}>
                {job.title}
              </option>
            ))}
          </select>
        </div>
        <button type="submit">Add Admit Card</button>
      </form>

      <div className="resource-admin-panel__list" ref={listRef} style={{ maxHeight: "800px", overflowY: "auto" }}>
        <h3>Admit Card Entries</h3>
        {isLoading ? (
          <div>Loading...</div>
        ) : (
          <ul>
            {filteredAdmitCards.map((admitCard) => (
              <li
                key={admitCard._id}
                style={{ display: "flex", alignItems: "center", gap: "1rem" }}
              >
                <span>{getJobTitle(admitCard.jobSlug)}</span>
                <span
                  style={{
                    fontWeight: "bold",
                    color: admitCard.status === "approved" ? "green" : "orange"
                  }}
                >
                  {admitCard.status === "approved" ? "Approved" : "Pending"}
                </span>
                {admitCard.status !== "approved" && (
                  <button
                    onClick={() => handleDelete(admitCard._id)}
                    style={{
                      backgroundColor: "#ff4d4f",
                      color: "white",
                      border: "none",
                      padding: "0.3rem 0.8rem",
                      borderRadius: "5px",
                      cursor: "pointer"
                    }}
                  >
                    Delete
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
        {isFetchingMore && <div style={{ textAlign: "center", margin: 10 }}>Loading more...</div>}
        {!threeTableHasMore.admitCards && !isLoading && threeTable.admitCards.length > 0 && <div style={{ textAlign: "center", margin: 10, color: "#888" }}>No more admit cards.</div>}
      </div>
    </div>
  );
}

export default AdministrativeAdmitCardAdminPanel;